if (!customElements.get('sticky-countdown-bar')) {
  customElements.define(
    'sticky-countdown-bar',
    class StickyCountdownBar extends HTMLElement {
      connectedCallback() {
        this.daysEl = this.querySelector('[data-unit="days"]');
        this.daysPart = this.querySelector('[data-part="days"]');
        this.hoursEl = this.querySelector('[data-unit="hours"]');
        this.minutesEl = this.querySelector('[data-unit="minutes"]');
        this.secondsEl = this.querySelector('[data-unit="seconds"]');
        this.timerMode = this.dataset.timerMode || 'expire_date';
        this.hideWhenExpired = this.dataset.hideWhenExpired === 'true';
        this.designMode = this.dataset.designMode === 'true';
        this.sessionKey = `sticky-countdown-bar-seen-${this.dataset.sectionId || 'default'}`;
        this.setBarHeight();

        if (this.shouldHideForSession()) {
          this.hideBar();
          return;
        }

        this.markSessionSeen();
        this.endTime = this.getEndTime();

        if (!this.endTime) {
          if (!this.designMode) this.hideBar();
          return;
        }

        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() {
        if (this.timer) clearInterval(this.timer);
      }

      isCollectionPage() {
        return this.dataset.pageType === 'collection';
      }

      shouldHideForSession() {
        if (this.designMode) return false;
        if (this.dataset.displayFrequency !== 'once_per_session') return false;
        if (!this.isCollectionPage()) return false;

        try {
          return Boolean(sessionStorage.getItem(this.sessionKey));
        } catch (error) {
          return false;
        }
      }

      markSessionSeen() {
        if (this.designMode) return;
        if (this.dataset.displayFrequency !== 'once_per_session') return;
        if (!this.isCollectionPage()) return;

        try {
          sessionStorage.setItem(this.sessionKey, '1');
        } catch (error) {
          // Ignore storage errors (private mode, disabled storage).
        }
      }

      getEndTime() {
        if (this.timerMode === 'daily') {
          return this.getNextDailyEndTime(this.dataset.dailyTime, this.dataset.tzOffset);
        }

        return this.parseExpireDate(this.dataset.expireDate, this.dataset.tzOffset);
      }

      formatOffset(tzOffset) {
        return (tzOffset || '+0000').replace(/([+-]\d{2})(\d{2})/, '$1:$2');
      }

      getShopDateParts(tzOffset) {
        const match = (tzOffset || '+0000').match(/^([+-])(\d{2})(\d{2})$/);
        if (!match) return null;

        const sign = match[1] === '-' ? -1 : 1;
        const offsetMinutes = sign * (Number(match[2]) * 60 + Number(match[3]));
        const shopNow = new Date(Date.now() + offsetMinutes * 60 * 1000);

        return {
          year: shopNow.getUTCFullYear(),
          month: shopNow.getUTCMonth(),
          day: shopNow.getUTCDate(),
        };
      }

      parseExpireDate(value, tzOffset) {
        if (!value) return null;

        const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?$/);
        if (!match) return null;

        const year = match[1];
        const month = match[2];
        const day = match[3];
        const hasTime = Boolean(match[4]);
        const hours = hasTime ? match[4] : '23';
        const minutes = hasTime ? match[5] : '59';
        const seconds = hasTime ? '00' : '59';
        const offset = this.formatOffset(tzOffset);
        const parsed = new Date(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offset}`);

        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }

      getNextDailyEndTime(value, tzOffset) {
        if (!value) return null;

        const match = value.trim().match(/^(\d{1,2}):(\d{2})$/);
        if (!match) return null;

        const hoursNum = Number(match[1]);
        const minutesNum = Number(match[2]);
        if (hoursNum > 23 || minutesNum > 59) return null;

        const parts = this.getShopDateParts(tzOffset);
        if (!parts) return null;

        const hours = String(hoursNum).padStart(2, '0');
        const minutes = String(minutesNum).padStart(2, '0');
        const offset = this.formatOffset(tzOffset);
        const pad = (number) => String(number).padStart(2, '0');

        const buildTarget = (year, month, day) =>
          new Date(`${year}-${pad(month + 1)}-${pad(day)}T${hours}:${minutes}:00${offset}`);

        let target = buildTarget(parts.year, parts.month, parts.day);
        if (Number.isNaN(target.getTime())) return null;

        if (target.getTime() <= Date.now()) {
          const nextDay = new Date(Date.UTC(parts.year, parts.month, parts.day + 1));
          target = buildTarget(nextDay.getUTCFullYear(), nextDay.getUTCMonth(), nextDay.getUTCDate());
        }

        return Number.isNaN(target.getTime()) ? null : target;
      }

      pad(value) {
        return String(Math.max(0, value)).padStart(2, '0');
      }

      tick() {
        const remaining = this.endTime.getTime() - Date.now();

        if (remaining <= 0) {
          if (this.timerMode === 'daily') {
            this.endTime = this.getNextDailyEndTime(this.dataset.dailyTime, this.dataset.tzOffset);
            if (this.endTime && this.endTime.getTime() > Date.now()) {
              this.tick();
              return;
            }
          }

          this.render(0, 0, 0, 0);
          if (this.timer) clearInterval(this.timer);
          if (this.hideWhenExpired && !this.designMode) this.hideBar();
          return;
        }

        const totalSeconds = Math.ceil(remaining / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        this.render(days, hours, minutes, seconds);
      }

      render(days, hours, minutes, seconds) {
        if (this.daysPart) this.daysPart.hidden = days < 1;
        if (this.daysEl) this.daysEl.textContent = this.pad(days);
        if (this.hoursEl) this.hoursEl.textContent = this.pad(hours);
        if (this.minutesEl) this.minutesEl.textContent = this.pad(minutes);
        if (this.secondsEl) this.secondsEl.textContent = this.pad(seconds);
      }

      setBarHeight() {
        const section = this.closest('.shopify-section');
        if (!section) return;
        document.documentElement.style.setProperty('--countdown-bar-height', Math.round(section.clientHeight) + 'px');
      }

      hideBar() {
        const section = this.closest('.shopify-section');
        if (section) section.style.display = 'none';
        document.documentElement.style.setProperty('--countdown-bar-height', '0px');
        document.documentElement.style.setProperty('--sticky-countdown-bar-enabled', '0');
      }
    }
  );
}
